import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import Map, { ViewState, ViewStateChangeEvent, Marker } from "react-map-gl";
import { Tooltip } from "@heroui/tooltip";
import "mapbox-gl/dist/mapbox-gl.css";
import { AutocompleteResult } from "@/types";
import { ProjectionSpecification } from "mapbox-gl";

interface GlobeComponentProps {
  selectedLocation: AutocompleteResult | null;
}

const mapAdjustment = 15;

const GlobeComponent: React.FC<GlobeComponentProps> = ({
  selectedLocation,
}) => {
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
  const [isMarkerShown, setIsMarkerShown] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [viewport, setViewport] = useState<ViewState>({
    longitude: 0,
    latitude: 20,
    zoom: 2,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const [isRotating, setIsRotating] = useState(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const lastTimeRef = useRef<number>(0);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const animationRef = useRef<number | null>(null);
  const projection: ProjectionSpecification = useMemo(
    () => ({
      name: "globe",
    }),
    []
  );

  const onMove = useCallback((evt: ViewStateChangeEvent) => {
    setViewport(evt.viewState);
  }, []);

  const rotateGlobe = useCallback((timestamp: number) => {
    if (!lastTimeRef.current) lastTimeRef.current = timestamp;
    const deltaTime = timestamp - lastTimeRef.current;
    lastTimeRef.current = timestamp;

    setViewport((prevViewport) => ({
      ...prevViewport,
      longitude: (prevViewport.longitude + deltaTime * 0.005) % 360,
    }));

    animationRef.current = requestAnimationFrame(rotateGlobe);
  }, []);

  useEffect(() => {
    if (isMapReady && isRotating) {
      animationRef.current = requestAnimationFrame(rotateGlobe);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isMapReady, rotateGlobe, isRotating]);

  useEffect(() => {
    if (selectedLocation && isMapReady && mapRef.current) {
      setIsRotating(false);
      const adjustedLon = Number(selectedLocation.longitude) - 4;
      const adjustedLat = Number(selectedLocation.latitude);
      mapRef.current.flyTo({
        center: [adjustedLon, adjustedLat],
        zoom: 4,
        duration: 1500,
        essential: true,
        pitch: 0,
      });

      setIsMarkerShown(false);

      const timer = setTimeout(() => {
        setIsMarkerShown(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [selectedLocation, isMapReady]);

  // reset globe on mount
  useEffect(() => {
    setViewport({
      longitude: 0,
      latitude: 20,
      zoom: 2,
      bearing: 0,
      pitch: 0,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
    });
  }, []);

  return (
    <div className="w-full h-full rounded-sm overflow-hidden relative">
      <Map
        mapboxAccessToken={mapboxToken}
        ref={(ref) => {
          if (ref) {
            mapRef.current = ref.getMap();
            setIsMapReady(true);
          }
        }}
        {...viewport}
        onMove={onMove}
        interactive={false}
        dragPan={false}
        dragRotate={false}
        scrollZoom={false}
        doubleClickZoom={false}
        keyboard={false}
        projection={projection}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/jamiedennis8/clxnxvbre00eu01ob2i06f337"
        attributionControl={false}
      >
        {selectedLocation && isMarkerShown && (
          <Marker
            longitude={selectedLocation.longitude}
            latitude={selectedLocation.latitude}
          >
            <Tooltip
              showArrow={true}
              isOpen={isMarkerShown}
              offset={10}
              content={
                <div
                  className={`p-1 flex flex-col items-center ${
                    isMarkerShown ? "" : "hidden"
                  }`}
                >
                  <h3 className="text-neutral-800 font-medium text-base">
                    {selectedLocation.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <p className="text-neutral-500 font-light text-xs">
                      {selectedLocation.address_components.country
                        ? selectedLocation.address_components.country
                        : selectedLocation.address_components.state}
                    </p>
                  </div>
                </div>
              }
            >
              <div className="w-2 h-2 rounded-full bg-white ring-2 ring-indigo-900"></div>
            </Tooltip>
          </Marker>
        )}
      </Map>
    </div>
  );
};

export default GlobeComponent;
